import { Component, computed, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { StoreLogo } from '../store-logo/store-logo';

/**
 * The site's coupon/offer card — the design the home page's "Trending Coupons" grid established:
 * a round store logo, an offer-type chip on a hairline rule with a Verified pill, the store name,
 * the offer title as body copy, then the expiry and a text CTA.
 *
 * Used everywhere coupons are listed **except the store page**, which keeps its own richer
 * `shared/coupon-card` (discount badge, badge pills, description, big code button).
 *
 * The visual treatment lives in the global `.sav-trending-coupon-*` rules in `styles.scss`, so this
 * component and the home page grid render identically by construction.
 */
@Component({
  selector: 'app-offer-card',
  imports: [RouterLink, StoreLogo],
  templateUrl: './offer-card.html',
  host: { class: 'sav-trending-coupon-card' },
})
export class OfferCard {
  readonly title = input('');
  readonly storeName = input('');
  /** '' when the store has none; the logo slot then shows the store name instead. */
  readonly logoUrl = input('');
  /** Route to the store's page; '' leaves the logo and name unlinked. */
  readonly storePath = input('');
  readonly expires = input('');

  /**
   * The card prints its own "Expires :" label, but some callers pass text that already carries one
   * (the shared coupon-card mapper returns "Expires: Jul 24, 2026"), which rendered as
   * "Expires : Expires: Jul 24, 2026". Strip any leading label here so every host is safe, and keep
   * "Expired" when that is what the text says.
   */
  protected readonly expiryLabel = computed(() =>
    /^\s*expired\b/i.test(this.expires()) ? 'Expired' : 'Expires',
  );

  protected readonly expiryValue = computed(() =>
    this.expires()
      .replace(/^\s*(expires|expired)\s*:?\s*/i, '')
      .trim(),
  );
  /** 'code' shows "Reveal Code"; anything else shows "Get Deal". */
  readonly ctaType = input<'code' | 'deal'>('deal');
  /**
   * Where the CTA points when the page has a plain URL for it. Pages that need to run something on
   * click (the /go/ redirect that records a click, for instance) either leave this unset, or
   * set it to the real destination and cancel the plain navigation from their `action` handler.
   */
  readonly ctaHref = input('');
  /**
   * Emitted when the CTA is used, for pages that open the offer themselves.
   *
   * The click event travels with it so a host that also sets `ctaHref` — keeping the anchor
   * middle-clickable and copyable — can `preventDefault()` and take over. Without an `ctaHref` the
   * card cancels the dead `#` navigation itself, so those hosts can ignore the event.
   */
  readonly action = output<Event>();

  protected onCta(event: Event): void {
    if (!this.ctaHref()) {
      event.preventDefault();
    }
    this.action.emit(event);
  }
}
